import type { Client } from '@accounts/transport';
import type { Ended, Entered } from './credentials';

export interface Factor {
	id: string;
	kind: 'totp' | 'passkey';
	label?: string;
	created: string;
	used?: string;
}

/** A sign-in on one device, with what it opened (ACC-07) */
export interface Device {
	handle: string;
	browser?: string;
	system?: string;
	place?: string;
	signed: string;
	seen: string;
	expires: string;
	current: boolean;
	products: { audience: string; signed: string; seen: string }[];
}

const bytes = (value: string) => Uint8Array.from(atob(value.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(value.length / 4) * 4, '=')), char => char.charCodeAt(0));
const text = (buffer: ArrayBuffer | null) => (buffer ? btoa(String.fromCharCode(...new Uint8Array(buffer))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '') : undefined);

/**
 * Passkeys in this browser: the service's options with their identifiers as bytes, and the browser's
 * answer back as base64url, which is what the service verifies
 */
export class Passkeys {
	static get supported(): boolean {
		return typeof globalThis.PublicKeyCredential === 'function' && Boolean(globalThis.navigator?.credentials);
	}

	static async create(options: Record<string, any>): Promise<unknown> {
		const publicKey = { ...options, challenge: bytes(options.challenge), user: { ...options.user, id: bytes(options.user.id) }, excludeCredentials: (options.excludeCredentials ?? []).map((entry: { id: string }) => ({ ...entry, id: bytes(entry.id) })) };
		const made = (await navigator.credentials.create({ publicKey: publicKey as unknown as PublicKeyCredentialCreationOptions })) as PublicKeyCredential;
		const response = made.response as AuthenticatorAttestationResponse;
		return { id: made.id, rawId: text(made.rawId), type: made.type, response: { clientDataJSON: text(response.clientDataJSON), attestationObject: text(response.attestationObject) } };
	}

	static async get(options: Record<string, any>): Promise<unknown> {
		const publicKey = { ...options, challenge: bytes(options.challenge), allowCredentials: (options.allowCredentials ?? []).map((entry: { id: string }) => ({ ...entry, id: bytes(entry.id) })) };
		const got = (await navigator.credentials.get({ publicKey: publicKey as unknown as PublicKeyCredentialRequestOptions })) as PublicKeyCredential;
		const response = got.response as AuthenticatorAssertionResponse;
		return { id: got.id, rawId: text(got.rawId), type: got.type, response: { clientDataJSON: text(response.clientDataJSON), authenticatorData: text(response.authenticatorData), signature: text(response.signature), userHandle: text(response.userHandle) } };
	}
}

/**
 * Second factors (E8), the second step of a sign-in, re-authentication in place, and the devices an
 * account is signed in on (ACC-07)
 */
export class Factors {
	#client: Client;

	constructor(client: Client) {
		this.#client = client;
	}

	async of(): Promise<{ factors: Factor[]; recovery: number }> {
		return this.#client.get('/v1/account/factors');
	}

	async totp(): Promise<{ id: string; secret: string; uri: string }> {
		return this.#client.post('/v1/account/factors/totp', {});
	}

	async confirm(id: string, code: string, label?: string): Promise<{ codes?: string[] }> {
		return this.#client.post(`/v1/account/factors/totp/${encodeURIComponent(id)}/confirmation`, { code, label });
	}

	async passkey(label?: string): Promise<{ id: string; codes?: string[] }> {
		const { publicKey } = await this.#client.post<{ publicKey: Record<string, unknown> }>('/v1/account/factors/passkeys/options', {});
		return this.#client.post('/v1/account/factors/passkeys', { credential: await Passkeys.create(publicKey), label });
	}

	async remove(id: string): Promise<{ last: boolean }> {
		return this.#client.delete(`/v1/account/factors/${encodeURIComponent(id)}`, { replay: false });
	}

	async codes(): Promise<{ codes: string[] }> {
		return this.#client.post('/v1/account/factors/recovery', {}, { replay: false });
	}

	/** Finishes a sign-in: `totp` or `recovery` with a code, `passkey` through this browser */
	async finish(ticket: string, method: string, code?: string): Promise<Entered> {
		if (method === 'passkey') {
			const { publicKey } = await this.#client.post<{ publicKey: Record<string, unknown> }>('/v1/sessions/factor/options', { ticket }, { ended: 'token' });
			return this.#client.post('/v1/sessions/factor', { ticket, method, assertion: await Passkeys.get(publicKey) }, { ended: 'token', replay: false });
		}
		return this.#client.post('/v1/sessions/factor', { ticket, method, code }, { ended: 'token', replay: false });
	}

	async methods(): Promise<string[]> {
		return (await this.#client.get<{ methods: string[] }>('/v1/sessions/current/reauthentication', { ended: 'caller' })).methods;
	}

	/** Proves it is the same person again; the session counts as just authenticated */
	async reauthenticate(method: string, value?: string): Promise<void> {
		if (method === 'passkey') {
			const { publicKey } = await this.#client.post<{ publicKey: Record<string, unknown> }>('/v1/sessions/current/reauthentication/options', {}, { ended: 'caller' });
			await this.#client.post('/v1/sessions/current/reauthentication', { method, assertion: await Passkeys.get(publicKey) }, { ended: 'caller', replay: false });
			return;
		}
		await this.#client.post('/v1/sessions/current/reauthentication', method === 'password' ? { method, password: value } : { method, code: value }, { ended: 'caller', replay: false });
	}

	async devices(): Promise<Device[]> {
		return (await this.#client.get<{ devices: Device[] }>('/v1/sessions')).devices;
	}

	async close(handle: string): Promise<{ ended: number }> {
		return this.#client.delete(`/v1/sessions/${encodeURIComponent(handle)}`, { replay: false });
	}

	/** Signs out every other session; with `access`, the person's AI apps and access tokens lose their access too (D196) */
	async others(access = false): Promise<{ ended: number; access?: Ended }> {
		return this.#client.post('/v1/sessions/others/end', access ? { access: 'end' } : {}, { replay: false });
	}
}
